import {
  ApprovalDecisionState,
  ApprovalStrength,
  UIChunkType,
} from '@myre/app-agent-domain';
import { type UiLibI18n } from '@myre/domain-i18n-wordings';
import { DefaultHeaderComponent, ModalService } from '@myre/ui-primitive-modal';

import {
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  type OnDestroy,
} from '@angular/core';
import { type SafeHtml } from '@angular/platform-browser';
import { isDataUIPart } from 'ai';

import { WORDINGS } from '../../../myre-wordings-loader';

import {
  type ApprovalDecision,
  type ApprovalEmailMessage,
  type ApprovalPlan,
  type ApprovalPlanChange,
  type ApprovalPlanFinding,
  type ApprovalPlanOperation,
  type ApprovalRequest,
  type EditedEmailContent,
  type MyreUiMessagePart,
} from '../../interfaces/chat.interfaces';
import { ChatContextService } from '../../services/chat-context.service';
import { FormatService } from '../../services/format.service';

import { EmailDraftModalComponent } from '../email-draft-modal/email-draft-modal.component';

/**
 * Renders the agent's human-in-the-loop commit gate as a Confirm/Cancel card.
 * The agent suspends the run with a graph `interrupt()` at a destructive write;
 * this card previews the plan (destructive operations + findings) so a human
 * can approve or abort BEFORE anything is persisted.
 *
 * On Confirm it emits the plan-bound `approvalToken` so the orchestrator can
 * resume the graph; on Cancel it emits a void `cancelled` event so the parent
 * can record the cancellation server-side — the suspended graph itself never
 * resumes, so no commit follows. The token is server-signed and rides in the
 * data part for the resume only; it is NEVER rendered to the user.
 *
 * Plan fields are rendered as escaped text (interpolation); the email body is the
 * one exception — the model writes markdown, rendered to sanitized HTML via the
 * same `FormatService` pipeline as chat messages (markdown-it + DOMPurify).
 *
 * When `confirmation.strength === 'strong'` (irreversible action, e.g. sending
 * an email), Confirm does NOT dispatch immediately. Instead it enters an
 * "arming" window of `graceSeconds` (default 3) during which the user sees a
 * single progress-bar countdown button to abort. If the window elapses the
 * decision is dispatched; if the user cancels within the window the card
 * reverts to its initial state (no decision recorded) so the user can retry.
 */
@Component({
  selector: 'app-approval-confirm',
  standalone: true,
  templateUrl: './approval-confirm.component.html',
  styleUrl: './approval-confirm.component.scss',
})
export class ApprovalConfirmComponent implements OnDestroy {
  public readonly part = input.required<MyreUiMessagePart>();
  public readonly streaming = input<boolean>(false);

  /**
   * The card is no longer the live gate: the user sent a new message instead of
   * deciding, so the pending request is dismissed (the suspended graph is never
   * resumed → nothing is committed). Locks the actions and shows a dismissed note.
   */
  public readonly superseded = input<boolean>(false);

  /** The user's approval — replayed by the orchestrator to resume the graph. */
  public readonly confirmed = output<ApprovalDecision>();

  /** The user's Cancel — the parent records it server-side so a reload shows the
   * card as cancelled, not as a re-armed pending gate. */
  public readonly cancelled = output<void>();

  protected readonly _decided = signal<ApprovalDecisionState | null>(null);

  /**
   * The effective decision driving the card's rendering: the live click
   * (`_decided`) or, on a reload, the decision persisted onto the part. Single
   * source of truth so a reloaded resolved card looks exactly like it did live.
   */
  protected readonly _resolvedDecision = computed<ApprovalDecisionState | null>(
    () => this._decided() ?? this._request()?.decision ?? null,
  );

  /** True while the strong-mode grace countdown is running. */
  protected readonly _arming = signal<boolean>(false);

  /** Progress 0→1 over the grace window (drives the CSS width of the fill bar). */
  protected readonly _armProgress = signal<number>(0);

  /** Whole seconds remaining, shown in the countdown button label. */
  protected readonly _armSecondsLeft = signal<number>(0);

  private readonly _request = computed<ApprovalRequest | null>(() => {
    const part = this.part();

    if (!isDataUIPart(part) || part.type !== UIChunkType.DataApproval) {
      return null;
    }

    return (part.data as { approval?: ApprovalRequest }).approval ?? null;
  });

  protected readonly _plan = computed<ApprovalPlan | null>(
    () => this._request()?.plan ?? null,
  );

  /**
   * The diff the commit would apply — the part of the card a human actually
   * decides on. Printed as it arrives: every string is already in the reader's
   * language, notation and currency unit, because only the server knows which
   * unit each of the two sides is counted in.
   */
  protected readonly _changes = computed<readonly ApprovalPlanChange[]>(
    () => this._plan()?.changes ?? [],
  );

  protected readonly _operations = computed<readonly ApprovalPlanOperation[]>(
    () => this._plan()?.operations ?? [],
  );

  protected readonly _findings = computed<readonly ApprovalPlanFinding[]>(
    () => this._plan()?.findings ?? [],
  );

  /**
   * The model-authored email, when this is a `send-approval`: the body hashed
   * into the approval token. It is what is sent unless the human edits the draft
   * in the modal, in which case `_editedDraft` overrides subject/body — both the
   * preview here and the payload applied server-side use the edited content, so
   * what the human arms-and-sends is always what they see.
   */
  protected readonly _message = computed<ApprovalEmailMessage | null>(
    () => this._plan()?.message ?? null,
  );

  private readonly _formatService = inject(FormatService);
  private readonly _modalService = inject(ModalService);
  private readonly _chatContextService = inject(ChatContextService);
  private readonly _wordings = inject<UiLibI18n>(WORDINGS);

  protected get _w() {
    return this._wordings._library.chat;
  }

  protected readonly _editedDraft = signal<EditedEmailContent | null>(null);

  /** Recipients shown on the card: the user's edit when present, else the model's. */
  protected readonly _displayTo = computed<readonly string[]>(
    () => this._editedDraft()?.to ?? this._message()?.to ?? [],
  );

  protected readonly _displayCc = computed<readonly string[]>(
    () => this._editedDraft()?.cc ?? this._message()?.cc ?? [],
  );

  /** Subject shown on the card: the user's edit when present, else the model's. */
  protected readonly _displaySubject = computed<string>(
    () => this._editedDraft()?.subject ?? this._message()?.subject ?? '',
  );

  /** >1 recipient ⇒ they are blind-copied and won't see each other. */
  protected readonly _recipientsPrivate = computed<boolean>(
    () => this._displayTo().length + this._displayCc().length > 1,
  );

  /** key → display name, from the model's attachments + the surfaced candidates. */
  private readonly _attachmentNameByKey = computed<Map<string, string>>(() => {
    const map = new Map<string, string>();
    const message = this._message();

    (message?.attachments ?? []).forEach((key, index) => {
      map.set(key, message?.attachmentNames?.[index] ?? key);
    });

    for (const doc of this._plan()?.availableAttachments ?? []) {
      map.set(doc.key, doc.name);
    }

    return map;
  });

  /** Attachments shown on the card: the user's edit when present, else the model's. */
  protected readonly _displayAttachments = computed<readonly string[]>(() => {
    const edited = this._editedDraft();

    if (edited) {
      return edited.attachments.map(
        key => this._attachmentNameByKey().get(key) ?? key,
      );
    }

    return [...(this._message()?.attachmentNames ?? [])];
  });

  /**
   * The email body rendered from markdown to sanitized HTML (the model writes
   * markdown; the frontend renders it safely — same pipeline as chat messages).
   */
  protected readonly _bodyHtml = computed<SafeHtml>(() =>
    this._formatService.parse(
      this._editedDraft()?.body ?? this._message()?.body ?? '',
    ),
  );

  protected readonly _hasBlockingFinding = computed(() =>
    this._findings().some(finding => finding.severity === 'error'),
  );

  protected readonly _prompt = computed(
    () => this._plan()?.prompt ?? this._w.approvalConfirmActionPrompt(),
  );

  protected readonly _strength = computed(
    () => this._plan()?.confirmation?.strength ?? ApprovalStrength.Normal,
  );

  protected readonly _graceSeconds = computed(
    () => this._plan()?.confirmation?.graceSeconds ?? 3,
  );

  /** Note shown when cancelled — authored per action, generic fallback. */
  protected readonly _cancelLabel = computed(
    () => this._plan()?.cancelLabel ?? this._w.approvalActionCancelled(),
  );

  protected get _locked(): boolean {
    return (
      this.streaming() || this.superseded() || this._resolvedDecision() !== null
    );
  }

  /** Interval handle for the ~50ms tick that drives arm progress. */
  private _armIntervalId: ReturnType<typeof setInterval> | null = null;

  /** Timestamp (ms) at which arming started — set at arm-start, cleared on cancel. */
  private _armStartedAt: number | null = null;

  constructor() {
    // When the card is superseded mid-arming, abort the countdown immediately.
    effect(() => {
      if (this.superseded() && this._arming()) {
        this._clearArmTimer();
        this._arming.set(false);
        this._armProgress.set(0);
        this._armSecondsLeft.set(0);
      }
    });
  }

  public ngOnDestroy(): void {
    this._clearArmTimer();
  }

  protected _onConfirm(): void {
    const request = this._request();

    if (!request || this._locked) {
      return;
    }

    if (this._strength() === ApprovalStrength.Strong) {
      this._startArming(request);

      return;
    }

    this._emitConfirmed(request);
  }

  /**
   * Dispatch the confirm with the action's own bubble label (from the plan), so
   * the human's choice reads contextually ("Envoi confirmé", "Modification
   * confirmée", …) instead of a single hardcoded string.
   */
  private _emitConfirmed(request: ApprovalRequest): void {
    this._decided.set(ApprovalDecisionState.Confirmed);
    const editedDraft = this._editedDraft();
    this.confirmed.emit({
      approvalToken: request.approvalToken,
      label: this._plan()?.confirmLabel ?? 'Confirmé',
      ...(editedDraft ? { editedEmailContent: editedDraft } : {}),
    });
  }

  protected _onEditDraft(): void {
    const message = this._message();

    if (!message) {
      return;
    }

    const customerId = this._chatContextService.context().params.customerId;
    const availableRecipients = this._plan()?.availableRecipients ?? [];
    const availableAttachments = this._plan()?.availableAttachments ?? [];

    this._modalService.openFactory(uid => ({
      key: 'email-draft.edit',
      size: 'large',
      zIndex: 10000002,
      context: {},
      header: {
        component: DefaultHeaderComponent,
        data: { title: this._w.emailDraftTitle() },
      },
      body: {
        component: EmailDraftModalComponent,
        bindings: {
          inputs: {
            uid,
            message,
            customerId,
            availableRecipients,
            availableAttachments,
          },
          outputs: {
            draftConfirmed: (draft: EditedEmailContent) => {
              this._editedDraft.set(draft);
              this._modalService.close(uid);
            },
          },
        },
      },
    }));
  }

  protected _onCancel(): void {
    if (!this._request() || this._locked) {
      return;
    }

    // Clean abort: the graph stays suspended and is never resumed, so no commit
    // is performed. Emit so the parent records the cancellation server-side — the
    // graph input never carries it, this is purely for a faithful reload.
    this._decided.set(ApprovalDecisionState.Cancelled);
    this.cancelled.emit();
  }

  /**
   * During strong-mode arming: the user clicked the countdown button to abort
   * this send attempt. Clear the timer and revert to the initial confirm/cancel
   * row — no decision is recorded (the card re-arms on next Confirm click).
   */
  protected _onArmCancel(): void {
    this._clearArmTimer();
    this._arming.set(false);
    this._armProgress.set(0);
    this._armSecondsLeft.set(0);
  }

  private _startArming(request: ApprovalRequest): void {
    const graceMs = this._graceSeconds() * 1000;
    this._armStartedAt = Date.now();
    this._arming.set(true);
    this._armProgress.set(0);
    this._armSecondsLeft.set(this._graceSeconds());

    this._armIntervalId = setInterval(() => {
      const elapsed = Date.now() - (this._armStartedAt ?? Date.now());
      const progress = Math.min(elapsed / graceMs, 1);
      const secondsLeft = Math.max(0, Math.ceil((graceMs - elapsed) / 1000));

      this._armProgress.set(progress);
      this._armSecondsLeft.set(secondsLeft);

      if (progress >= 1) {
        this._clearArmTimer();
        this._arming.set(false);
        this._emitConfirmed(request);
      }
    }, 50);
  }

  private _clearArmTimer(): void {
    if (this._armIntervalId !== null) {
      clearInterval(this._armIntervalId);
      this._armIntervalId = null;
    }

    this._armStartedAt = null;
  }
}
